import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, Activity, ChevronRight, Clock, ClipboardCheck,
  Package, RefreshCw, TrendingDown, CheckCircle2, Loader2, Printer, BookOpen, FileBarChart, Monitor,
  Download,
} from "lucide-react";
import { api } from "../lib/api";
import { openAuthenticatedPdf } from "../lib/pdfDownloader";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import OrphanSlotsCard from "../components/OrphanSlotsCard";
import { toast } from "sonner";

/**
 * Vue d'ensemble "État des distributeurs" (Phase 38).
 * Affiche en un coup d'œil l'état de chaque machine pour préparer la tournée.
 */
const STATUS_STYLES = {
  URGENT:  { bg: "bg-[#FBEEEA] border-[#E8B0A0]",   chip: "bg-[#C05C47] text-white",     label: "Urgent",     Icon: AlertTriangle },
  WARNING: { bg: "bg-[#FBF4E9] border-[#E8D3A8]",   chip: "bg-[#D4A373] text-[#5A3D1A]", label: "À surveiller", Icon: Clock },
  OK:      { bg: "bg-white border-[#E5E5E0]",        chip: "bg-[#E8F2EC] text-[#1F4E3D]", label: "OK",        Icon: CheckCircle2 },
};

function FillBar({ pct, status }) {
  const color = status === "URGENT" ? "bg-[#C05C47]" : status === "WARNING" ? "bg-[#D4A373]" : "bg-[#1F4E3D]";
  return (
    <div className="h-2 bg-[#F0F0EB] rounded-full overflow-hidden">
      <div
        className={`h-full ${color} transition-all duration-500`}
        style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
      />
    </div>
  );
}

export default function DistributorsOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/distributors/overview");
      setData(r.data);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const [pdfBusy, setPdfBusy] = useState(false);

  const onPdfClick = (endpoint, filename) => async () => {
    setPdfBusy(true);
    try {
      await openAuthenticatedPdf({ url: endpoint, action: "preview", filename });
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Impossible de générer le PDF : " + (e.message || "erreur inconnue"));
    } finally {
      setPdfBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-[#575A57]" data-testid="overview-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement de l'état des distributeurs…
      </div>
    );
  }

  if (!data) return null;
  const { distributors, summary } = data;

  return (
    <div className="space-y-6 pb-12" data-testid="distributors-overview-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl text-[#1A1C1A] font-semibold flex items-center gap-2">
            <Activity className="w-7 h-7 text-[#1F4E3D]" />
            État des distributeurs
          </h1>
          <p className="text-sm text-[#575A57] mt-1">
            Vue temps réel pour préparer les tournées de réassort. {summary.total} distributeur(s) automatique(s) suivis.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => window.open("/vitrine-machines", "_blank")}
            className="border-[#C1A063] text-[#8C6B2F] hover:bg-[#FDF6E7]"
            data-testid="overview-vitrine-btn"
            title="Écran mural plein écran style vitrine — épuisés barrés, live 60s (idéal pour l'atelier)"
          >
            <Monitor className="w-4 h-4 mr-2" />
            Vitrine machines
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/operator-guide.pdf`;
              window.open(url, "_blank");
            }}
            data-testid="overview-operator-guide-btn"
            title="Guide opérateur imprimable (3 pages) à remettre à l'équipe terrain"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Guide opérateur (PDF)
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/distributors/production-plan.pdf`;
              window.open(url, "_blank");
            }}
            data-testid="overview-production-plan-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            Bon de préparation production (PDF)
          </Button>
          <Button variant="outline" onClick={load} disabled={refreshing} data-testid="overview-refresh-btn">
            {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Actualiser
          </Button>
          <Button
            onClick={() => {
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/distributors/inventory-sheets-all.pdf`;
              window.open(url, "_blank");
            }}
            className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
            data-testid="overview-inventory-sheets-btn"
            title="Feuille d'inventaire imprimable par machine — cases vides à remplir à la main pendant la tournée"
          >
            <ClipboardCheck className="w-4 h-4 mr-2" />
            Feuilles d'inventaire (PDF)
          </Button>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryKpi label="Urgent" value={summary.urgent} tone="danger" Icon={AlertTriangle} testid="kpi-urgent" />
        <SummaryKpi label="À surveiller" value={summary.warning} tone="warning" Icon={Clock} testid="kpi-warning" />
        <SummaryKpi label="OK" value={summary.ok} tone="ok" Icon={CheckCircle2} testid="kpi-ok" />
        <SummaryKpi label="Total" value={summary.total} tone="default" Icon={Package} testid="kpi-total" />
      </div>

      {/* Phase 101dn — Audit des lots orphelins (sans slot en machine automatique) */}
      <OrphanSlotsCard />

      {/* List */}
      {distributors.length === 0 ? (
        <div className="text-center py-12 text-[#575A57]">
          Aucun distributeur automatique configuré.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {distributors.map((d) => {
            const s = STATUS_STYLES[d.status] || STATUS_STYLES.OK;
            return (
              <div
                key={d.id}
                className={`border rounded-lg p-4 ${s.bg}`}
                data-testid={`overview-card-${d.id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <Link
                        to={`/distributeurs/${d.id}/reassort`}
                        className="font-medium text-[#1A1C1A] hover:text-[#1F4E3D] flex items-center gap-1"
                        data-testid={`overview-name-${d.id}`}
                      >
                        {d.name}
                        <ChevronRight className="w-3.5 h-3.5 opacity-50" />
                      </Link>
                      {d.code && <Badge variant="outline" className="font-mono text-[10px]">{d.code}</Badge>}
                    </div>
                    {d.city && <p className="text-xs text-[#575A57]">{d.city}</p>}
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${s.chip}`}
                    data-testid={`status-${d.id}`}
                  >
                    <s.Icon className="w-3 h-3" />
                    {s.label}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#575A57] mb-0.5">Remplissage</div>
                    <div className="text-2xl font-mono font-semibold text-[#1A1C1A]" data-testid={`fill-${d.id}`}>
                      {d.current_qty}<span className="text-sm text-[#A3A6A3]">/{d.max_capacity}</span>
                    </div>
                    <div className="text-[10px] text-[#575A57]">{d.fill_pct}% plein</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#575A57] mb-0.5">Ventes 7j</div>
                    <div className="text-2xl font-mono font-semibold text-[#1F4E3D]" data-testid={`sales7d-${d.id}`}>
                      {d.sales_7d}
                    </div>
                    <div className="text-[10px] text-[#575A57]">barquettes</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#575A57] mb-0.5">À recharger</div>
                    <div className={`text-2xl font-mono font-semibold ${d.restock_suggestion_qty > 0 ? "text-[#8C6B2F]" : "text-[#A3A6A3]"}`} data-testid={`suggest-${d.id}`}>
                      {d.restock_suggestion_qty}
                    </div>
                    <div className="text-[10px] text-[#575A57]">suggéré</div>
                  </div>
                </div>

                <FillBar pct={d.fill_pct} status={d.status} />

                <div className="flex items-center justify-between mt-3 text-xs">
                  <div className="text-[#575A57] flex items-center gap-2 flex-wrap">
                    {d.alerts_dlc > 0 && (
                      <span className="inline-flex items-center gap-1 text-[#C05C47]" data-testid={`dlc-alert-${d.id}`}>
                        <TrendingDown className="w-3 h-3" />
                        {d.alerts_dlc} barq. DLC ≤ 3j
                      </span>
                    )}
                    {d.last_session_at ? (
                      <span className="inline-flex items-center gap-1" data-testid={`last-session-${d.id}`}>
                        <Clock className="w-3 h-3" />
                        {d.days_since_last_session === 0
                          ? "Tournée aujourd'hui"
                          : d.days_since_last_session === 1
                            ? "Hier"
                            : `Il y a ${d.days_since_last_session}j`}
                        {d.last_session_operator && ` · ${d.last_session_operator}`}
                      </span>
                    ) : (
                      <span className="text-[#A3A6A3]">Aucune tournée enregistrée</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-7 border-[#1F4E3D] text-[#1F4E3D] hover:bg-[#E8F2EC]"
                      onClick={() => {
                        const url = `${process.env.REACT_APP_BACKEND_URL}/api/distributors/${d.id}/inventory-sheet.pdf`;
                        window.open(url, "_blank");
                      }}
                      data-testid={`inventory-sheet-${d.id}`}
                      title="Feuille d'inventaire imprimable pour cette machine (cases vides à remplir)"
                    >
                      <Printer className="w-3 h-3 mr-1" />
                      Inventaire
                    </Button>
                    <MonthlyReportButton distributorId={d.id} distributorName={d.name} onBusy={setPdfBusy} pdfBusy={pdfBusy} />
                    <Link to={`/distributeurs/${d.id}/reassort`}>
                      <Button variant="outline" size="sm" className="text-xs h-7" data-testid={`go-restock-${d.id}`}>
                        <ClipboardCheck className="w-3 h-3 mr-1" />
                        Nouvelle tournée
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SummaryKpi({ label, value, tone, Icon, testid }) {
  const toneMap = {
    danger:  "bg-[#FBEEEA] border-[#E8B0A0] text-[#A04E2C]",
    warning: "bg-[#FBF4E9] border-[#E8D3A8] text-[#8C6B2F]",
    ok:      "bg-[#E8F2EC] border-[#A8C9B6] text-[#1F4E3D]",
    default: "bg-white border-[#E5E5E0] text-[#1A1C1A]",
  };
  return (
    <div className={`border rounded-lg p-4 ${toneMap[tone]}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
          <div className="text-3xl font-mono font-semibold mt-1">{value}</div>
        </div>
        <Icon className="w-5 h-5 opacity-60" />
      </div>
    </div>
  );
}

/**
 * Bouton "Rapport mensuel" : popover avec sélecteur de mois (12 derniers) puis
 * génère le PDF authentifié via openAuthenticatedPdf.
 */
function MonthlyReportButton({ distributorId, distributorName, onBusy, pdfBusy }) {
  const now = new Date();
  const months = [];
  for (let i = 0; i < 12; i++) {
    const dt = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      year: dt.getFullYear(),
      month: dt.getMonth() + 1,
      label: dt.toLocaleDateString("fr-CH", { month: "long", year: "numeric" }),
    });
  }

  const handleGenerate = async (year, month) => {
    onBusy(true);
    try {
      const safe = (distributorName || "distributeur").replace(/[^a-z0-9]/gi, "_");
      const fname = `rapport-${safe}-${year}-${String(month).padStart(2, "0")}.pdf`;
      await openAuthenticatedPdf({
        url: `/api/distributors/${distributorId}/monthly-report.pdf?year=${year}&month=${month}`,
        action: "preview",
        filename: fname,
      });
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Impossible de générer le rapport : " + (e.message || "erreur inconnue"));
    } finally {
      onBusy(false);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-7"
          disabled={pdfBusy}
          data-testid={`monthly-report-${distributorId}`}
          title="Rapport mensuel partenaire (PDF imprimable)"
        >
          <FileBarChart className="w-3 h-3 mr-1" />
          Rapport mensuel
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-2" data-testid="monthly-report-popover">
        <div className="text-[10px] uppercase tracking-wider text-[#575A57] mb-2 px-1">
          Choisir un mois
        </div>
        <div className="max-h-72 overflow-y-auto flex flex-col gap-0.5">
          {months.map((m, i) => (
            <Button
              key={`${m.year}-${m.month}`}
              variant="ghost"
              size="sm"
              className="text-xs h-7 justify-start font-normal"
              onClick={() => handleGenerate(m.year, m.month)}
              disabled={pdfBusy}
              data-testid={`month-option-${m.year}-${m.month}`}
            >
              {i === 0 && <span className="text-[9px] uppercase tracking-wider text-[#1F4E3D] mr-1.5">En cours</span>}
              <span className="capitalize">{m.label}</span>
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
